export const BULK_MOVE_SEARCH_FIELDS = [
    { id: 'palletId', label: 'Pallet ID', match: 'exact', placeholder: 'Enter pallet ID' },
    { id: 'lotId', label: 'Lot ID', match: 'exact', placeholder: 'Enter lot ID' },
    { id: 'externalSku', label: 'External SKU', match: 'exact', placeholder: 'Enter external SKU' },
    { id: 'packagingSku', label: 'Packaging SKU', match: 'exact', placeholder: 'Enter packaging ID' },
    { id: 'itemName', label: 'Item Name', match: 'fuzzy', placeholder: 'Enter at least 3 characters' },
    { id: 'packagingName', label: 'Packaging Name', match: 'fuzzy', placeholder: 'Enter at least 3 characters' },
];

const FUZZY_MIN_LENGTH = 3;

export function parsePositiveIntFilter(value) {
    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }

    const parsed = Number(trimmed);
    if (!Number.isInteger(parsed) || parsed <= 0) {
        return null;
    }

    return parsed;
}

/** @deprecated Use parsePositiveIntFilter */
export function parseExternalSkuFilter(sku) {
    return parsePositiveIntFilter(sku);
}

function getSearchField(searchBy) {
    return BULK_MOVE_SEARCH_FIELDS.find((field) => field.id === searchBy);
}

function appendSearchFilter(filters, searchBy, searchTerm) {
    const field = getSearchField(searchBy);
    if (!field) {
        return;
    }

    const trimmedTerm = searchTerm.trim();
    if (!trimmedTerm) {
        return;
    }

    if (field.match === 'exact') {
        const parsed = parsePositiveIntFilter(trimmedTerm);
        if (parsed === null) {
            return;
        }

        if (searchBy === 'palletId') {
            filters.push({ searchBy: 'palletId', searchTerm: parsed });
            return;
        }

        if (searchBy === 'lotId') {
            filters.push({ searchBy: 'lotId', searchTerm: parsed });
            return;
        }

        if (searchBy === 'externalSku') {
            filters.push({ searchBy: 'externalSku', searchTerm: parsed });
            filters.push({ searchBy: 'type', searchTerm: 'ITEM' });
            return;
        }

        if (searchBy === 'packagingSku') {
            filters.push({ searchBy: 'externalSku', searchTerm: parsed });
            filters.push({ searchBy: 'type', searchTerm: 'PACKAGING' });
        }

        return;
    }

    if (trimmedTerm.length < FUZZY_MIN_LENGTH) {
        return;
    }

    filters.push({ searchBy, searchTerm: trimmedTerm });
}

/**
 * Builds warehouse inventory v2 getPallets filter params for the bulk move modal.
 *
 * @param {{ locationId: string | number, recipeCardsOnly?: boolean, searchBy?: string, searchTerm?: string }} options
 * @returns {Array<{ searchBy: string, searchTerm: string | number }>}
 */
export function buildPalletFilters({ locationId, recipeCardsOnly = false, searchBy = 'palletId', searchTerm = '' }) {
    const filters = [{ searchBy: 'locationId', searchTerm: locationId }];

    appendSearchFilter(filters, searchBy, searchTerm);

    if (recipeCardsOnly) {
        filters.push({ searchBy: 'recipeCardsOnly', searchTerm: 'true' });
    }

    return filters;
}

export function getInitialBulkMoveSearchState(lotId) {
    if (lotId) {
        return { searchBy: 'lotId', searchTerm: String(lotId) };
    }

    return { searchBy: 'palletId', searchTerm: '' };
}

export function isBulkMoveSearchFieldExact(searchBy) {
    return getSearchField(searchBy)?.match === 'exact';
}

export function hasActivePalletContentFilter({ recipeCardsOnly = false, searchBy = 'palletId', searchTerm = '' }) {
    if (recipeCardsOnly) {
        return true;
    }

    const searchFilters = [];
    appendSearchFilter(searchFilters, searchBy, searchTerm);
    return searchFilters.length > 0;
}

export function getBulkMoveModalHeading({
    locationId,
    locationName,
    recipeCardsOnly = false,
    searchBy = 'palletId',
    searchTerm = '',
}) {
    const locationLabel = locationName || locationId;

    if (searchBy === 'lotId') {
        const activeLotId = parsePositiveIntFilter(searchTerm);
        if (activeLotId !== null) {
            return `Pallets for Lot ${activeLotId} in Location ${locationLabel}`;
        }
    }

    if (hasActivePalletContentFilter({ searchBy, searchTerm, recipeCardsOnly })) {
        return `Matching Pallets in Location ${locationLabel}`;
    }

    return `All Pallets in Location ${locationLabel}`;
}

export function getBulkMoveEmptyStateMessage({
    locationId,
    locationName,
    recipeCardsOnly = false,
    searchBy = 'palletId',
    searchTerm = '',
}) {
    const locationLabel = locationName || locationId;

    if (hasActivePalletContentFilter({ searchBy, searchTerm, recipeCardsOnly })) {
        return 'No pallets match the current search or filters. Try adjusting your search or clearing filters.';
    }

    return `There are no pallets in location ${locationLabel}.`;
}
